import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Camera,
  Keyboard,
  Mic,
  Info,
  Loader2,
  X,
  ZoomIn,
} from 'lucide-react';
import type {
  ConfirmedReading,
  ExtractionResult,
  Formulation,
  Instrument,
  Measurement,
  MeasurementKey,
  RawReadingValue,
  Source,
} from '../lib/types';
import { INSTRUMENT_FIELDS, MEASUREMENT_FIELDS, MEASUREMENT_KEYS } from '../lib/types';
import { formatValue, parseNumber } from '../lib/format';
import { hasValue, warningsFor } from '../lib/validation';
import type { Warning } from '../lib/validation';
import { applyMeasurements, buildMeasurement, createFormulation, emptyDraft } from '../services/formulations';
import { useAuth } from '../hooks/useAuth';
import { confirmCapture, createCapture, newCaptureId, rejectCapture } from '../services/captures';
import { resolveImageUrl } from '../services/storage';
import { Badge, Button, Input, Select, cx } from './ui';
import PhotoNote from './PhotoNote';
import ReaderConfirmation from './ReaderConfirmation';
import type { ReaderExtraction } from '../services/readings';

/**
 * One shape for all three input modes.
 * Photo and voice arrive with an extraction; manual arrives empty. All of them
 * land on this screen, and nothing reaches a formulation until Confirm.
 */
export interface ConfirmationSource {
  mode: Source;
  /** null only for a manual entry, whose capture record is written on confirm. */
  captureId: string | null;
  instrument: Instrument;
  /** Which of the capturer's readers this was read against, if any. */
  readerId?: string | null;
  readerName?: string | null;
  extraction: ExtractionResult | null;
  transcript: string | null;
  /** Local object URL while the photo is still in memory. */
  previewUrl: string | null;
  /** Storage path, used when a capture is reopened from history. */
  storagePath: string | null;
  targetFormulationId: string | null;
  focusKey: MeasurementKey | null;
  /** Extraction still in flight — the screen shows progress rather than empty fields. */
  extracting?: boolean;
  /**
   * Photo uploaded, nothing read yet, waiting for the user to say "read it".
   *
   * The pause is the point: it is the only moment where a note can reach the
   * first extraction. The person holding the camera knows things the image does
   * not carry — that the third row is a repeat, that only the highlighted rows
   * matter — and saying so before the read beats correcting a dozen fields
   * after it. Leaving the note empty and reading straight away is one tap.
   */
  awaitingNote?: boolean;
  /** What the app is doing, so the wait is legible rather than a bare spinner. */
  progress?: { phase: 'compressing' | 'uploading' | 'reading'; fraction?: number };
  error?: string | null;
}

interface RowState {
  localId: string;
  sampleLabel: string | null;
  /** Metadata read from the same row — applied when a formulation is created. */
  meta: { np: number | null; modification: string | null; measuredAt: string | null };
  formulationId: string | typeof NEW_FORMULATION | '';
  newLabel: string;
  values: Partial<Record<MeasurementKey, { value: string; sd: string }>>;
  notMeasured: Set<MeasurementKey>;
  unreadable: Set<MeasurementKey>;
}

const NEW_FORMULATION = '__new__' as const;

const MODE_META: Record<Source, { icon: typeof Camera; label: string }> = {
  photo: { icon: Camera, label: 'Photo' },
  voice: { icon: Mic, label: 'Voice' },
  manual: { icon: Keyboard, label: 'Manual' },
};

function toText(v: number | null | undefined, key: MeasurementKey): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '';
  return formatValue(v, key);
}

/**
 * Which fields this screen shows.
 *
 * A capture shows exactly what its instrument measures, and nothing else. Size,
 * PDI and zeta are read on the Zetasizer; encapsulation efficiency is run on
 * separate equipment on a separate computer, so no single source carries both.
 * This used to widen the list to include anything the model returned, which
 * meant an EE column visible in a Zetasizer photo — an old sheet with both
 * pasted together — could be confirmed into a formulation it was never measured
 * against. The server strips those values now; this makes sure the screen would
 * not offer them even if one arrived.
 *
 * Manual entry is the exception: it shows every field, because it is also the
 * correction path for a value the instrument path got wrong.
 */
function visibleFields(instrument: Instrument, mode: Source, _extraction: ExtractionResult | null) {
  if (mode === 'manual') return [...MEASUREMENT_KEYS];
  const allowed = new Set<MeasurementKey>(INSTRUMENT_FIELDS[instrument]);
  return MEASUREMENT_KEYS.filter((k) => allowed.has(k));
}

function buildInitialRows(
  source: ConfirmationSource,
  formulations: Formulation[],
  fields: MeasurementKey[],
): RowState[] {
  const unreadable = new Set(
    (source.extraction?.unreadableFields ?? [])
      .map((f) => f.toLowerCase().trim())
      .filter((f): f is MeasurementKey => (MEASUREMENT_KEYS as readonly string[]).includes(f)),
  );

  const readings = source.extraction?.readings?.length ? source.extraction.readings : [null];

  return readings.map((reading, i) => {
    // Match the label the instrument printed against formulations we already have.
    const label = reading?.sampleLabel ?? null;
    const matched =
      source.targetFormulationId ??
      (label
        ? formulations.find(
            (f) => f.label.trim().toLowerCase() === label.trim().toLowerCase(),
          )?.id
        : undefined);

    const existing = formulations.find((f) => f.id === matched);
    const values: RowState['values'] = {};

    for (const key of fields) {
      const fromModel = reading?.[key] as RawReadingValue | null | undefined;
      if (fromModel && !unreadable.has(key)) {
        values[key] = { value: toText(fromModel.value, key), sd: toText(fromModel.sd, key) };
      } else if (source.mode === 'manual' && existing?.[key]) {
        // Manual editing starts from what is already recorded.
        const m = existing[key] as Measurement;
        values[key] = { value: toText(m.value, key), sd: toText(m.sd, key) };
      } else {
        values[key] = { value: '', sd: '' };
      }
    }

    // With a label the model read off the screen, default to filing it: reuse
    // the matching formulation, or create one under that name. Only a capture
    // with no label at all still needs the user to choose.
    const defaultTarget = matched
      ? matched
      : label?.trim()
        ? NEW_FORMULATION
        : formulations.length === 1
          ? formulations[0].id
          : '';

    return {
      localId: `row-${i}`,
      sampleLabel: label,
      meta: {
        np: reading?.np ?? null,
        modification: reading?.modification ?? null,
        measuredAt: reading?.measuredAt ?? null,
      },
      formulationId: defaultTarget,
      newLabel: label ?? '',
      values,
      notMeasured: new Set<MeasurementKey>(),
      unreadable,
    };
  });
}

export default function ConfirmationScreen({
  projectId,
  formulations,
  source,
  onClose,
  onReExtract,
}: {
  projectId: string;
  formulations: Formulation[];
  source: ConfirmationSource;
  onClose: () => void;
  /** Re-running extraction with a spoken note replaces this screen's source. */
  onReExtract?: (next: ConfirmationSource) => void;
}) {
  const fields = useMemo(
    () => visibleFields(source.instrument, source.mode, source.extraction),
    [source.instrument, source.mode, source.extraction],
  );

  /**
   * A reader-driven read replaces this screen entirely: its shape is per-reading
   * records against user-defined fields, which the row model below cannot hold.
   */
  const [readerResult, setReaderResult] = useState<ReaderExtraction | null>(null);

  const [rows, setRows] = useState<RowState[]>(() =>
    buildInitialRows(source, formulations, fields),
  );
  const [signAcknowledged, setSignAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(source.previewUrl);
  const [reReading, setReReading] = useState(false);
  const { user } = useAuth();
  const [zoom, setZoom] = useState(1);

  // Extraction can land after the screen opens; rebuild the fields when it does.
  useEffect(() => {
    setRows(buildInitialRows(source, formulations, fields));
    // formulations intentionally excluded: a live snapshot update must not
    // discard edits the user has already made on this screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.extraction, source.captureId, fields]);

  useEffect(() => {
    if (source.previewUrl) {
      setImageUrl(source.previewUrl);
    } else if (source.storagePath) {
      resolveImageUrl(source.storagePath).then(setImageUrl).catch(() => setImageUrl(null));
    }
  }, [source.previewUrl, source.storagePath]);

  const confidence = source.extraction?.confidence;
  const ModeIcon = MODE_META[source.mode].icon;

  const setValue = (
    localId: string,
    key: MeasurementKey,
    part: 'value' | 'sd',
    raw: string,
  ) =>
    setRows((rs) =>
      rs.map((r) =>
        r.localId === localId
          ? {
              ...r,
              values: { ...r.values, [key]: { ...(r.values[key] ?? { value: '', sd: '' }), [part]: raw } },
              // Typing into a field un-marks it as unreadable or not-measured.
              unreadable: new Set([...r.unreadable].filter((k) => k !== key)),
              notMeasured: new Set([...r.notMeasured].filter((k) => k !== key)),
            }
          : r,
      ),
    );

  const toggleNotMeasured = (localId: string, key: MeasurementKey) =>
    setRows((rs) =>
      rs.map((r) => {
        if (r.localId !== localId) return r;
        const next = new Set(r.notMeasured);
        if (next.has(key)) next.delete(key);
        else {
          next.add(key);
        }
        return {
          ...r,
          notMeasured: next,
          values: next.has(key) ? { ...r.values, [key]: { value: '', sd: '' } } : r.values,
        };
      }),
    );

  const setRowField = (localId: string, patch: Partial<RowState>) =>
    setRows((rs) => rs.map((r) => (r.localId === localId ? { ...r, ...patch } : r)));

  /**
   * Fields this entry is expected to account for.
   * A capture must account for everything its instrument produces. Editing one
   * cell by hand only has to account for that cell — the other fields are shown
   * so they can be corrected, not demanded.
   */
  const requiredFields = useMemo(() => {
    if (source.mode === 'manual' && source.focusKey) return [source.focusKey];
    if (source.mode === 'manual') {
      return fields.filter((k) => INSTRUMENT_FIELDS[source.instrument].includes(k));
    }
    return fields;
  }, [source.mode, source.focusKey, source.instrument, fields]);

  /** Every expected field must be either filled or explicitly marked not measured. */
  const blockers = useMemo(() => {
    const out: string[] = [];
    rows.forEach((row, i) => {
      const who = rows.length > 1 ? `Reading ${i + 1}: ` : '';
      if (!row.formulationId) out.push(`${who}choose which sample this belongs to.`);
      if (row.formulationId === NEW_FORMULATION && !row.newLabel.trim())
        out.push(`${who}name the new sample.`);

      const undecided = requiredFields.filter(
        (key) =>
          !row.notMeasured.has(key) &&
          parseNumber(row.values[key]?.value ?? '') === null,
      );
      if (undecided.length === requiredFields.length) {
        out.push(`${who}enter at least one value, or mark the fields not measured.`);
      } else if (undecided.length > 0) {
        out.push(
          `${who}${undecided
            .map((k) => MEASUREMENT_FIELDS[k].shortLabel)
            .join(', ')} — fill in or mark "not measured".`,
        );
      }
    });

    // A minus sign is the difference between two opposite results, and speech
    // recognition drops it readily. Make the user look at it on purpose.
    if (source.mode === 'voice' && !signAcknowledged) {
      const anyZeta = rows.some((r) => parseNumber(r.values.zeta?.value ?? '') !== null);
      if (anyZeta) out.push('Confirm the zeta sign is correct.');
    }
    return out;
  }, [rows, requiredFields, source.mode, signAcknowledged]);

  async function handleConfirm() {
    if (blockers.length > 0) return;
    setBusy(true);
    setSaveError(null);
    try {
      // A manual entry gets a capture record too, so provenance stays uniform:
      // every number in the workbook traces back to something.
      let captureId = source.captureId;
      if (!captureId) {
        captureId = newCaptureId(projectId);
        await createCapture(projectId, captureId, {
          mode: 'manual',
          instrument: source.instrument,
          capturedByUid: user?.uid ?? '',
          capturedByName: user?.displayName ?? user?.email ?? null,
          status: 'pending',
        });
      }

      const appliedTo: string[] = [];
      const confirmedReadings: ConfirmedReading[] = [];

      for (const row of rows) {
        let formulationId = row.formulationId;
        if (formulationId === NEW_FORMULATION) {
          const parsedDate = row.meta.measuredAt ? new Date(`${row.meta.measuredAt}T12:00:00`) : null;
          formulationId = await createFormulation(projectId, {
            ...emptyDraft(),
            label: row.newLabel.trim(),
            np: row.meta.np,
            modification: row.meta.modification ?? '',
            measuredAt:
              parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : new Date(),
          });
        }
        if (!formulationId) continue;

        const patch: Partial<Record<MeasurementKey, Measurement | null>> = {};
        const confirmedValues: ConfirmedReading['values'] = {};

        for (const key of fields) {
          if (row.notMeasured.has(key)) {
            patch[key] = null;
            confirmedValues[key] = null;
            continue;
          }
          const value = parseNumber(row.values[key]?.value ?? '');
          if (value === null) continue; // untouched: leave whatever is already there
          const sd = parseNumber(row.values[key]?.sd ?? '');
          patch[key] = buildMeasurement(key, value, sd, source.mode, captureId);
          confirmedValues[key] = { value, sd };
        }

        await applyMeasurements(projectId, formulationId, patch);
        appliedTo.push(formulationId);
        confirmedReadings.push({
          formulationId,
          sampleLabel: row.sampleLabel,
          values: confirmedValues,
          notMeasured: [...row.notMeasured],
        });
      }

      await confirmCapture(
        projectId,
        captureId,
        { readings: confirmedReadings },
        appliedTo,
        source.extraction?.columnsSeen,
      );
      onClose();
    } catch (e) {
      setSaveError((e as Error)?.message ?? 'Could not save. Nothing was written.');
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    setBusy(true);
    try {
      if (source.captureId) await rejectCapture(projectId, source.captureId);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  const showSource = source.mode !== 'manual';

  /**
   * A reader-driven read takes over the screen. Its records are per-reading
   * against fields this component knows nothing about, and grouping ninety-six
   * of them by sample is a different job from confirming twelve rows of four
   * fixed measurements.
   */
  if (readerResult && source.captureId && user?.uid) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-ink-950" data-confirming="true">
        <header
          className="flex items-center gap-3 border-b border-ink-800 px-4 pb-3"
          style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
        >
          <h2 className="text-sm font-semibold">Confirm readings</h2>
          <button
            onClick={onClose}
            className="ml-auto rounded-lg p-2 text-ink-400 hover:bg-ink-800 hover:text-ink-100"
            aria-label="Close without saving"
          >
            <X size={18} />
          </button>
        </header>
        <ReaderConfirmation
          projectId={projectId}
          captureId={source.captureId}
          uid={user.uid}
          extraction={readerResult}
          onDone={onClose}
          onCancel={onClose}
        />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-ink-950" data-confirming="true">
      <header
        className="flex items-center gap-3 border-b border-ink-800 px-4 pb-3"
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
      >
        <ModeIcon size={18} className="text-accent-400" />
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold">
            {source.mode === 'manual' ? 'Enter measurements' : 'Confirm measurements'}
          </h2>
          <p className="text-xs text-ink-400">
            {source.mode === 'manual'
              ? 'Typed values are recorded with the same provenance as captured ones.'
              : 'Nothing is written until you confirm.'}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {confidence && <ConfidenceBadge confidence={confidence} />}
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-ink-400 hover:bg-ink-800 hover:text-ink-100"
            aria-label="Close without saving"
          >
            <X size={18} />
          </button>
        </div>
      </header>

      <div className={cx('flex min-h-0 flex-1 flex-col overflow-hidden', showSource && 'lg:flex-row')}>
        {showSource && (
          <section className="max-h-[38vh] shrink-0 overflow-auto border-b border-ink-800 bg-ink-900 lg:max-h-none lg:w-[42%] lg:border-b-0 lg:border-r">
            {source.mode === 'photo' ? (
              <div className="relative h-full">
                {imageUrl ? (
                  <div className="h-full overflow-auto p-3 [touch-action:pinch-zoom]">
                    <img
                      src={imageUrl}
                      alt="Captured instrument screen"
                      onClick={() => setZoom((z) => (z >= 4 ? 1 : z * 2))}
                      style={{ width: `${zoom * 100}%` }}
                      className="mx-auto max-w-none cursor-zoom-in rounded-lg"
                    />
                  </div>
                ) : (
                  <div className="flex h-full min-h-40 items-center justify-center text-sm text-ink-400">
                    Image unavailable offline — the values below are still editable.
                  </div>
                )}
                {imageUrl && (
                  <div className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-1 rounded-md bg-black/70 px-2 py-1 text-xs text-ink-300">
                    <ZoomIn size={12} />
                    {zoom === 1 ? 'Tap to zoom' : `${zoom}×`}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4">
                <span className="field-label">Transcript as heard</span>
                <p className="mt-2 rounded-lg border border-ink-700 bg-ink-800 p-3 font-mono text-sm leading-relaxed text-ink-100">
                  {source.transcript || <span className="text-ink-400">No speech captured.</span>}
                </p>
                <p className="mt-2 text-xs text-ink-400">
                  Stored verbatim with this capture. Check it against the fields — speech
                  recognition substitutes homophones silently.
                </p>
              </div>
            )}
          </section>
        )}

        <section className="min-h-0 flex-1 overflow-y-auto p-4">
          {source.mode === 'photo' && source.captureId && !source.extracting && (
            <PhotoNote
              projectId={projectId}
              captureId={source.captureId}
              storagePath={source.storagePath}
              instrument={source.instrument}
              existingNote={source.transcript}
              firstRead={source.awaitingNote === true}
              readerId={source.readerId ?? null}
              onReaderExtracted={(result) => setReaderResult(result)}
              onReExtracted={(result, note) =>
                onReExtract?.({
                  ...source,
                  extraction: result,
                  transcript: note || source.transcript,
                  error: null,
                  awaitingNote: false,
                })
              }
              onFailed={(message) =>
                onReExtract?.({ ...source, error: message, awaitingNote: false })
              }
              onBusyChange={setReReading}
            />
          )}

          {source.awaitingNote ? (
            /* Nothing has been read yet, so there are no values to show and
               nothing to confirm. The note panel above is the whole screen. */
            <p className="rounded-lg border border-ink-800 bg-ink-900 p-3 text-xs text-ink-400">
              The photo is uploaded. Add a note if it helps, then read it — the values appear here.
            </p>
          ) : source.extracting ? (
            <CaptureProgress mode={source.mode} progress={source.progress} />
          ) : (
            <>
              {source.error && (
                <div className="mb-4 flex gap-2 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Extraction failed</p>
                    <p className="text-red-300">{source.error}</p>
                    <p className="mt-1 text-red-300">
                      The capture is kept. Enter the values by hand below.
                    </p>
                  </div>
                </div>
              )}

              {confidence === 'low' && (
                <div className="mb-4 flex gap-2 rounded-lg border border-amber-800 bg-amber-950 p-3 text-sm text-amber-200">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>
                    The model rated its own reading <strong>low confidence</strong>. Check every
                    digit against the {source.mode === 'photo' ? 'photo' : 'transcript'}.
                  </span>
                </div>
              )}

              <div className="space-y-5">
                {rows.map((row, i) => (
                  <ReadingCard
                    key={row.localId}
                    index={i}
                    total={rows.length}
                    row={row}
                    fields={fields}
                    mode={source.mode}
                    focusKey={source.focusKey}
                    formulations={formulations}
                    onValue={setValue}
                    onToggleNotMeasured={toggleNotMeasured}
                    onRowField={setRowField}
                  />
                ))}
              </div>

              {source.mode === 'voice' &&
                rows.some((r) => parseNumber(r.values.zeta?.value ?? '') !== null) && (
                  <SignCheck
                    rows={rows}
                    acknowledged={signAcknowledged}
                    onAcknowledge={setSignAcknowledged}
                  />
                )}
            </>
          )}
        </section>
      </div>

      <footer
        className="border-t border-ink-800 bg-ink-900 px-4 pt-3"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
      >
        {saveError && (
          <p className="mb-2 rounded-lg border border-red-800 bg-red-950 p-2 text-sm text-red-200">
            {saveError}
          </p>
        )}
        {blockers.length > 0 && !source.extracting && (
          <ul className="mb-2 space-y-0.5 text-xs text-ink-400">
            {blockers.slice(0, 3).map((b) => (
              <li key={b} className="flex items-start gap-1.5">
                <Info size={12} className="mt-0.5 shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-end gap-2">
          {source.captureId && source.mode !== 'manual' && (
            <Button variant="ghost" onClick={() => void handleReject()} disabled={busy}>
              Reject
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="success"
            icon={<Check size={16} />}
            loading={busy}
            disabled={blockers.length > 0 || source.extracting || reReading}
            onClick={() => void handleConfirm()}
          >
            Confirm and save
          </Button>
        </div>
      </footer>
    </div>
  );
}

/**
 * Upload has a real percentage; reading does not — the model returns when it
 * returns. Showing a fake bar for that would be a lie, so the reading phase
 * animates and reports elapsed time instead.
 */
function CaptureProgress({
  mode,
  progress,
}: {
  mode: Source;
  progress?: { phase: 'compressing' | 'uploading' | 'reading'; fraction?: number };
}) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const id = window.setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, []);

  const phase = progress?.phase ?? 'reading';
  const label =
    phase === 'compressing'
      ? 'Preparing the photo'
      : phase === 'uploading'
        ? 'Uploading the photo'
        : mode === 'photo'
          ? 'Reading the values'
          : 'Reading the transcript';

  const pct =
    phase === 'uploading' && typeof progress?.fraction === 'number'
      ? Math.round(progress.fraction * 100)
      : null;

  const steps: Array<{ key: string; text: string }> = [
    { key: 'compressing', text: 'Prepare' },
    { key: 'uploading', text: 'Upload' },
    { key: 'reading', text: 'Read' },
  ];
  const activeIndex = steps.findIndex((s2) => s2.key === phase);

  return (
    <div className="rounded-xl border border-ink-700 bg-ink-900 p-4">
      <div className="flex items-center gap-3">
        <Loader2 size={18} className="shrink-0 animate-spin text-accent-400" />
        <span className="text-sm font-medium text-ink-100">{label}</span>
        <span className="ml-auto text-xs tabular-nums text-ink-400">
          {pct !== null ? `${pct}%` : `${elapsed}s`}
        </span>
      </div>

      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-ink-800">
        {pct !== null ? (
          <div
            className="h-full rounded-full bg-accent-500 transition-[width] duration-200"
            style={{ width: `${pct}%` }}
          />
        ) : (
          <div className="h-full w-1/3 animate-[shimmer_1.4s_ease-in-out_infinite] rounded-full bg-accent-500" />
        )}
      </div>

      <div className="mt-3 flex gap-4">
        {steps.map((s2, i) => (
          <span
            key={s2.key}
            className={cx(
              'text-xs',
              i < activeIndex ? 'text-emerald-400' : i === activeIndex ? 'text-ink-100' : 'text-ink-600',
            )}
          >
            {i < activeIndex ? '✓ ' : ''}
            {s2.text}
          </span>
        ))}
      </div>

      {phase === 'reading' && elapsed > 12 && (
        <p className="mt-3 text-xs text-ink-400">
          A full table takes longer than a single reading — every row is transcribed separately.
        </p>
      )}
    </div>
  );
}

function ConfidenceBadge({ confidence }: { confidence: 'high' | 'medium' | 'low' }) {
  const tone = confidence === 'high' ? 'good' : confidence === 'medium' ? 'warn' : 'bad';
  return (
    <Badge tone={tone} className={confidence === 'low' ? 'animate-pulse' : undefined}>
      {confidence === 'low' && <AlertTriangle size={12} />}
      {confidence} confidence
    </Badge>
  );
}

function ReadingCard({
  index,
  total,
  row,
  fields,
  mode,
  focusKey,
  formulations,
  onValue,
  onToggleNotMeasured,
  onRowField,
}: {
  index: number;
  total: number;
  row: RowState;
  fields: MeasurementKey[];
  mode: Source;
  focusKey: MeasurementKey | null;
  formulations: Formulation[];
  onValue: (localId: string, key: MeasurementKey, part: 'value' | 'sd', raw: string) => void;
  onToggleNotMeasured: (localId: string, key: MeasurementKey) => void;
  onRowField: (localId: string, patch: Partial<RowState>) => void;
}) {
  return (
    <div className="card p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {total > 1 && (
          <Badge tone="neutral">
            Reading {index + 1} of {total}
          </Badge>
        )}
        {row.sampleLabel && (
          <Badge tone={row.formulationId === NEW_FORMULATION ? 'info' : 'good'}>
            {row.formulationId === NEW_FORMULATION ? 'new' : 'matched'}
            <span className="font-mono">{row.sampleLabel}</span>
          </Badge>
        )}
      </div>

      <div className="mb-4">
        <label className="field-label mb-1.5" htmlFor={`target-${row.localId}`}>
          Applies to
        </label>
        {row.sampleLabel && row.formulationId === NEW_FORMULATION && (
          <p className="mb-1.5 text-xs text-ink-400">
            No sample is called “{row.sampleLabel}” yet, so one will be created. Change the
            name below, or pick an existing sample instead.
          </p>
        )}
        {row.formulationId === NEW_FORMULATION &&
          (row.meta.np !== null || row.meta.modification || row.meta.measuredAt) && (
            <p className="mb-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-400">
              <span className="text-ink-500">also read:</span>
              {row.meta.np !== null && <span>N/P {row.meta.np}</span>}
              {row.meta.modification && <span>{row.meta.modification}</span>}
              {row.meta.measuredAt && <span>{row.meta.measuredAt}</span>}
            </p>
          )}
        <Select
          id={`target-${row.localId}`}
          value={row.formulationId}
          className={row.formulationId ? undefined : 'border-amber-700'}
          onChange={(e) =>
            onRowField(row.localId, {
              formulationId: (e.target as HTMLSelectElement).value as RowState['formulationId'],
            })
          }
        >
          <option value="">Choose a sample…</option>
          {formulations.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label || 'Untitled'}
            </option>
          ))}
          <option value={NEW_FORMULATION}>+ New sample…</option>
        </Select>
        {row.formulationId === NEW_FORMULATION && (
          <Input
            className="mt-2"
            autoFocus
            value={row.newLabel}
            onChange={(e) => onRowField(row.localId, { newLabel: e.target.value })}
            placeholder="Name for the new sample"
          />
        )}
      </div>

      <div className="space-y-3">
        {fields.map((key) => (
          <FieldRow
            key={key}
            fieldKey={key}
            row={row}
            mode={mode}
            autoFocus={focusKey === key}
            onValue={onValue}
            onToggleNotMeasured={onToggleNotMeasured}
          />
        ))}
      </div>
    </div>
  );
}

function FieldRow({
  fieldKey,
  row,
  mode,
  autoFocus,
  onValue,
  onToggleNotMeasured,
}: {
  fieldKey: MeasurementKey;
  row: RowState;
  mode: Source;
  autoFocus: boolean;
  onValue: (localId: string, key: MeasurementKey, part: 'value' | 'sd', raw: string) => void;
  onToggleNotMeasured: (localId: string, key: MeasurementKey) => void;
}) {
  const spec = MEASUREMENT_FIELDS[fieldKey];
  const cell = row.values[fieldKey] ?? { value: '', sd: '' };
  const isUnreadable = row.unreadable.has(fieldKey);
  const notMeasured = row.notMeasured.has(fieldKey);
  const parsed: RawReadingValue = { value: parseNumber(cell.value), sd: parseNumber(cell.sd) };
  const warnings: Warning[] = notMeasured ? [] : warningsFor(fieldKey, parsed);
  const isEmpty = !hasValue(parsed) && !notMeasured;

  return (
    <div
      className={cx(
        'rounded-lg border p-3 transition-colors',
        isUnreadable
          ? 'border-amber-700 bg-amber-950/30'
          : notMeasured
            ? 'border-ink-700 bg-ink-800/40 opacity-70'
            : 'border-ink-700 bg-ink-800/30',
      )}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">
          {spec.label}
          {spec.unit && <span className="ml-1 text-ink-400">({spec.unit})</span>}
        </span>
        {isUnreadable && (
          <Badge tone="warn">
            <AlertTriangle size={11} />
            could not read — enter it yourself
          </Badge>
        )}
        <button
          type="button"
          onClick={() => onToggleNotMeasured(row.localId, fieldKey)}
          className={cx(
            'ml-auto rounded-md px-2 py-1 text-xs transition-colors',
            notMeasured
              ? 'bg-ink-600 text-ink-100'
              : 'text-ink-400 hover:bg-ink-700 hover:text-ink-100',
          )}
        >
          {notMeasured ? 'Marked not measured' : 'Not measured'}
        </button>
      </div>

      <div className="grid grid-cols-[1fr_1fr] gap-2">
        <div>
          <label className="field-label mb-1" htmlFor={`${row.localId}-${fieldKey}-value`}>
            Value
          </label>
          <Input
            id={`${row.localId}-${fieldKey}-value`}
            type="text"
            inputMode="decimal"
            autoFocus={autoFocus}
            disabled={notMeasured}
            value={cell.value}
            onChange={(e) => onValue(row.localId, fieldKey, 'value', e.target.value)}
            placeholder={notMeasured ? '—' : ''}
            className={cx(
              'tabular-nums',
              spec.signCritical && mode === 'voice' && 'text-lg font-semibold',
              isEmpty && 'border-amber-700/70',
            )}
          />
        </div>
        <div>
          <label className="field-label mb-1" htmlFor={`${row.localId}-${fieldKey}-sd`}>
            SD
          </label>
          <Input
            id={`${row.localId}-${fieldKey}-sd`}
            type="text"
            inputMode="decimal"
            disabled={notMeasured}
            value={cell.sd}
            onChange={(e) => onValue(row.localId, fieldKey, 'sd', e.target.value)}
            placeholder={notMeasured ? '—' : 'optional'}
            className="tabular-nums"
          />
        </div>
      </div>

      {warnings.map((w) => (
        <p
          key={w.message}
          className={cx(
            'mt-2 flex items-start gap-1.5 text-xs',
            w.severity === 'warn' ? 'text-amber-300' : 'text-ink-400',
          )}
        >
          {w.severity === 'warn' ? (
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          ) : (
            <Info size={12} className="mt-0.5 shrink-0" />
          )}
          {w.message} <span className="text-ink-500">Saved anyway if you confirm.</span>
        </p>
      ))}
    </div>
  );
}

/** Voice drops minus signs. Show the sign at a size nobody can skim past. */
function SignCheck({
  rows,
  acknowledged,
  onAcknowledge,
}: {
  rows: RowState[];
  acknowledged: boolean;
  onAcknowledge: (v: boolean) => void;
}) {
  const zetas = rows
    .map((r) => parseNumber(r.values.zeta?.value ?? ''))
    .filter((v): v is number => v !== null);

  return (
    <div className="mt-5 rounded-xl border border-amber-700 bg-amber-950/40 p-4">
      <div className="flex items-start gap-2">
        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-300" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-100">Check the zeta sign</p>
          <p className="mt-0.5 text-xs text-amber-200/80">
            Speech recognition drops “minus” readily, and the result still looks plausible.
          </p>

          <div className="mt-3 flex flex-wrap gap-3">
            {zetas.map((z, i) => (
              <div
                key={i}
                className="rounded-lg border border-amber-700 bg-ink-900 px-4 py-2 text-center"
              >
                <span
                  className={cx(
                    'font-mono text-3xl font-bold tabular-nums',
                    z < 0 ? 'text-sky-300' : 'text-rose-300',
                  )}
                >
                  {z > 0 ? '+' : ''}
                  {z.toFixed(2)}
                </span>
                <p className="mt-0.5 text-xs text-ink-400">{z < 0 ? 'negative' : 'positive'} mV</p>
              </div>
            ))}
          </div>

          <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-amber-100">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => onAcknowledge(e.target.checked)}
              className="size-4 rounded border-amber-600 bg-ink-800 accent-amber-500"
            />
            The sign shown above is correct.
          </label>
        </div>
      </div>
    </div>
  );
}
